<script setup>
import { reactive, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ContactSelector from 'dashboard/components-next/ContactSelector/ContactSelector.vue';
import PhoneNumberUploader from 'dashboard/components-next/PhoneNumberUploader/PhoneNumberUploader.vue';
import VariableTextArea from './VariableTextArea.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'cancel']);
const { t } = useI18n();
const store = useStore();

const formState = {
  uiFlags: useMapGetter('buhotextCampaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  devices: useMapGetter('buhotextCampaigns/getDevices'),
};

const state = reactive({
  name: '',
  content: '',
  device_id: null,
  sim_subscription_id: null,
  segment: 'labels', // labels, custom, bulk
  personalized_send: false,
  labels_filter: [],
  labelType: 'conversation', // 'conversation' o 'contact'
  selected_labels: [],
  phone_numbers: '',
  contact_ids: '',
});

const rules = {
  name: { required, minLength: minLength(1) },
  content: { required, minLength: minLength(1) },
  device_id: { required },
  sim_subscription_id: { required },
  segment: { required },
  labels_filter: {},
  phone_numbers: {},
  contact_ids: {},
};

const v$ = useVuelidate(rules, state);

const isSubmitting = computed(
  () => formState.uiFlags.value.isCreating || formState.uiFlags.value.isUpdating
);

const deviceOptions = computed(() =>
  formState.devices.value.map(device => ({
    value: device.id,
    label: `${device.name} (${device.device_brand} ${device.device_model})`,
  }))
);

// SIM options del dispositivo seleccionado
const selectedDevice = computed(() => {
  if (!state.device_id) return null;
  return formState.devices.value.find(d => d.id === state.device_id);
});

const simOptions = computed(() => {
  if (!selectedDevice.value?.sims) return [];
  return selectedDevice.value.sims
    .map(sim => ({
      value: parseInt(sim.subscription_id, 10),
      label: `${sim.carrier_name} (${sim.phone_number})`,
    }))
    .filter(option => Number.isInteger(option.value) && option.value > 0);
});

// Auto-seleccionar primera SIM cuando se cambia dispositivo
watch(
  () => state.device_id,
  (newDeviceId, oldDeviceId) => {
    if (newDeviceId && newDeviceId !== oldDeviceId) {
      if (simOptions.value.length > 0) {
        state.sim_subscription_id = simOptions.value[0].value;
      }
    }
  }
);

// Opciones de audiencia visibles en UI
const segmentOptions = computed(() => [
  { value: 'labels', label: t('CAMPAIGN.BUHOTEXT.FORM.SEGMENT.LABELS') },
  { value: 'bulk', label: t('CAMPAIGN.BUHOTEXT.FORM.SEGMENT.BULK') },
  { value: 'custom', label: t('CAMPAIGN.BUHOTEXT.FORM.SEGMENT.CUSTOM') },
]);

const labelsOptions = computed(() =>
  formState.labels.value.map(label => ({
    value: label.title,
    label: label.title,
  }))
);

const messageLength = computed(() => state.content.length);
const messageChars = computed(() => {
  const smsLength = 160;
  const count = Math.ceil(messageLength.value / smsLength) || 1;
  return { length: messageLength.value, sms: count };
});

const shouldShowVariableComposer = computed(
  () =>
    state.segment === 'custom' ||
    (state.segment === 'labels' && state.personalized_send)
);

const variableOptions = computed(() => [
  {
    label: t('CAMPAIGN.BUHOTEXT.FORM.MESSAGE.VAR_NAME'),
    value: '{{contact.name}}',
  },
  {
    label: t('CAMPAIGN.BUHOTEXT.FORM.MESSAGE.VAR_PHONE'),
    value: '{{contact.phone_number}}',
  },
]);

const getErrorMessage = field => {
  if (v$.value[field].$error) {
    return t(`CAMPAIGN.BUHOTEXT.FORM.${field.toUpperCase()}.ERROR`);
  }
  return '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name'),
  content: getErrorMessage('content'),
  device_id: getErrorMessage('device_id'),
  sim_subscription_id: getErrorMessage('sim_subscription_id'),
  segment: getErrorMessage('segment'),
}));

const isSubmitDisabled = computed(
  () => v$.value.$invalid || isSubmitting.value
);

const submitButtonLabel = computed(() => {
  if (isSubmitting.value) {
    return t('CAMPAIGN.BUHOTEXT.FORM.SENDING');
  }

  if (props.isCreating) {
    return t('CAMPAIGN.BUHOTEXT.FORM.SEND');
  }

  return t('CAMPAIGN.BUHOTEXT.FORM.UPDATE');
});

const loadFormData = () => {
  if (props.campaign) {
    state.name = props.campaign.name;
    state.content = props.campaign.content || props.campaign.message || '';
    state.device_id = props.campaign.device_id;
    state.sim_subscription_id = props.campaign.sim_subscription_id ?? null;

    // 66text guarda números en settings.phone_numbers (array), campañas locales en phone_numbers (string)
    const apiPhoneNumbers = props.campaign.settings?.phone_numbers;
    const localPhoneNumbers = props.campaign.phone_numbers;
    const hasApiPhoneNumbers = apiPhoneNumbers?.length > 0;
    const hasLocalPhoneNumbers =
      localPhoneNumbers && localPhoneNumbers.trim().length > 0;
    const campaignContactIds =
      props.campaign.contact_ids || props.campaign.contacts_ids;
    const hasContactIds = campaignContactIds?.length > 0;
    const normalizedApiPhoneNumbers = Array.isArray(apiPhoneNumbers)
      ? apiPhoneNumbers.join('\n')
      : apiPhoneNumbers;
    const normalizedContactIds = Array.isArray(campaignContactIds)
      ? campaignContactIds.join(',')
      : campaignContactIds || '';

    const savedSegment =
      props.campaign.segment === 'all' ? 'bulk' : props.campaign.segment;
    const isVisibleSegment = ['labels', 'custom', 'bulk'].includes(
      savedSegment
    );

    if (isVisibleSegment) {
      state.segment = savedSegment;
      state.phone_numbers = '';
      if (hasApiPhoneNumbers) {
        state.phone_numbers = normalizedApiPhoneNumbers;
      } else if (hasLocalPhoneNumbers) {
        state.phone_numbers = localPhoneNumbers;
      }
      state.contact_ids = hasContactIds ? normalizedContactIds : '';
    } else if (hasApiPhoneNumbers) {
      state.segment = 'bulk';
      state.phone_numbers = Array.isArray(apiPhoneNumbers)
        ? apiPhoneNumbers.join('\n')
        : apiPhoneNumbers;
      state.contact_ids = '';
    } else if (hasLocalPhoneNumbers) {
      state.segment = 'bulk';
      state.phone_numbers = localPhoneNumbers;
      state.contact_ids = '';
    } else if (hasContactIds) {
      state.segment = 'custom';
      state.phone_numbers = '';
      state.contact_ids = Array.isArray(campaignContactIds)
        ? campaignContactIds.join(',')
        : campaignContactIds || '';
    } else {
      state.segment = savedSegment || 'labels';
      state.phone_numbers = '';
      state.contact_ids = '';
    }

    state.labels_filter = props.campaign.labels_filter || [];
    state.labelType = props.campaign.label_type || 'conversation';
    state.selected_labels = props.campaign.selected_labels || [];
    state.personalized_send = false;
  } else {
    state.name = '';
    state.content = '';
    state.device_id = null;
    state.sim_subscription_id = null;
    state.segment = 'labels';
    state.personalized_send = false;
    state.labels_filter = [];
    state.labelType = 'conversation';
    state.selected_labels = [];
    state.phone_numbers = '';
    state.contact_ids = '';
  }
  v$.value.$reset();
};

const handleSubmit = async event => {
  // Prevenir submit si es evento del form
  if (event && typeof event === 'object' && event.preventDefault) {
    event.preventDefault();
  }

  const isValid = await v$.value.$validate();
  if (!isValid) return;

  // Evita enviar campañas con SIM inválida (ej. null/NaN).
  if (
    !Number.isInteger(state.sim_subscription_id) ||
    state.sim_subscription_id <= 0
  ) {
    v$.value.sim_subscription_id.$touch();
    return;
  }

  // Si segment es 'custom', mantenemos 'custom' para activar el flujo personalizado
  // Si segment es 'labels', enviamos al backend para que extraiga los números según las etiquetas
  // Si segment es 'bulk', enviamos números cargados/subidos directamente
  let finalSegment = state.segment;
  let finalPhoneNumbers = state.phone_numbers;
  let finalContactIds = null;
  let labelFilterData = null;

  if (state.segment === 'custom') {
    finalSegment = 'custom';
    finalPhoneNumbers = state.contact_ids; // Ya contiene los números de teléfono
    finalContactIds = state.contact_ids;
  } else if (state.segment === 'labels') {
    // El backend se encargará de filtrar contactos por etiquetas y extraer números
    finalSegment = 'labels';
    labelFilterData = {
      label_type: state.labelType,
      selected_labels: state.selected_labels,
    };
  }

  const formData = {
    name: state.name,
    content: state.content,
    device_id: state.device_id,
    sim_subscription_id: state.sim_subscription_id,
    segment: finalSegment,
    phone_numbers: finalPhoneNumbers || null,
    contact_ids: finalContactIds,
    label_type: labelFilterData?.label_type || null,
    selected_labels: labelFilterData?.selected_labels || null,
    personalized_send: false,
  };

  if (state.segment === 'custom') {
    formData.personalized_send = true;
  } else if (state.segment === 'labels') {
    formData.personalized_send = state.personalized_send;
  }

  emit('save', formData);
};

watch(
  () => props.campaign,
  () => {
    loadFormData();
  },
  { immediate: true }
);

// Limpiar las etiquetas seleccionadas cuando cambie el tipo de etiqueta
watch(
  () => state.labelType,
  (newType, oldType) => {
    if (newType !== oldType) {
      state.selected_labels = [];
    }
  }
);

onMounted(() => {
  store.dispatch('buhotextCampaigns/fetchDevices');
  store.dispatch('labels/get');
});
</script>

<template>
  <div class="bg-n-alpha-3 rounded-xl border border-n-weak p-6">
    <div class="mb-6">
      <h2 class="text-lg font-semibold text-n-slate-12 mb-1">
        {{
          isCreating
            ? $t('CAMPAIGN.BUHOTEXT.CREATE.TITLE')
            : $t('CAMPAIGN.BUHOTEXT.EDIT.TITLE')
        }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('CAMPAIGN.BUHOTEXT.EDITOR.SUBTITLE') }}
      </p>
    </div>

    <form class="space-y-6" @submit.prevent="handleSubmit(false)">
      <!-- Información básica -->
      <div class="space-y-4">
        <h3
          class="text-sm font-semibold text-n-slate-12 uppercase tracking-wide"
        >
          {{ $t('CAMPAIGN.BUHOTEXT.EDITOR.BASIC_INFO') }}
        </h3>

        <Input
          v-model="state.name"
          :label="$t('CAMPAIGN.BUHOTEXT.FORM.NAME.LABEL')"
          :placeholder="$t('CAMPAIGN.BUHOTEXT.FORM.NAME.PLACEHOLDER')"
          :error="formErrors.name"
        />

        <ComboBox
          v-model="state.device_id"
          :label="$t('CAMPAIGN.BUHOTEXT.FORM.DEVICE_ID.LABEL')"
          :placeholder="$t('CAMPAIGN.BUHOTEXT.FORM.DEVICE_ID.PLACEHOLDER')"
          :options="deviceOptions"
          :error="formErrors.device_id"
        />

        <ComboBox
          v-model="state.sim_subscription_id"
          :label="$t('CAMPAIGN.BUHOTEXT.FORM.SIM_SUBSCRIPTION_ID.LABEL')"
          :placeholder="
            $t('CAMPAIGN.BUHOTEXT.FORM.SIM_SUBSCRIPTION_ID.PLACEHOLDER')
          "
          :options="simOptions"
          :error="formErrors.sim_subscription_id"
          :disabled="!state.device_id || simOptions.length === 0"
        />
      </div>

      <!-- Audiencia -->
      <div class="space-y-4">
        <h3
          class="text-sm font-semibold text-n-slate-12 uppercase tracking-wide"
        >
          {{ $t('CAMPAIGN.BUHOTEXT.EDITOR.AUDIENCE') }}
        </h3>

        <ComboBox
          v-model="state.segment"
          :label="$t('CAMPAIGN.BUHOTEXT.FORM.SEGMENT.LABEL')"
          :placeholder="$t('CAMPAIGN.BUHOTEXT.FORM.SEGMENT.PLACEHOLDER')"
          :options="segmentOptions"
          :error="formErrors.segment"
        />

        <div
          class="text-xs text-n-slate-11 bg-n-blue-3 border border-n-blue-6 rounded-lg p-3"
        >
          <i class="i-lucide-info mr-1" />
          {{ $t('CAMPAIGN.BUHOTEXT.FORM.SEGMENT.HELP') }}
        </div>

        <div v-if="state.segment === 'bulk'" class="space-y-3">
          <PhoneNumberUploader v-model="state.phone_numbers" />

          <TextArea
            v-model="state.phone_numbers"
            :label="$t('CAMPAIGN.BUHOTEXT.FORM.PHONE_NUMBERS.LABEL')"
            :placeholder="
              $t('CAMPAIGN.BUHOTEXT.FORM.PHONE_NUMBERS.PLACEHOLDER')
            "
            rows="4"
          />

          <p class="text-xs text-n-slate-11">
            {{ $t('CAMPAIGN.BUHOTEXT.FORM.PHONE_NUMBERS.HELP') }}
          </p>
        </div>

        <div v-if="state.segment === 'custom'" class="space-y-3">
          <ContactSelector v-model="state.contact_ids" return-phone-numbers />
        </div>

        <!-- Filtrado por Etiquetas -->
        <div v-if="state.segment === 'labels'" class="space-y-4">
          <!-- Tipo de etiqueta -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('CAMPAIGN.BUHOTEXT.FORM.LABEL_TYPE.LABEL') }}
            </label>
            <div class="flex gap-0 rounded-lg overflow-hidden p-1 bg-n-alpha-1">
              <button
                type="button"
                class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
                :class="[
                  state.labelType === 'conversation'
                    ? 'bg-n-brand text-white shadow-sm'
                    : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
                ]"
                @click="state.labelType = 'conversation'"
              >
                {{ $t('CAMPAIGN.BUHOTEXT.FORM.LABEL_TYPE.CONVERSATION') }}
              </button>
              <button
                type="button"
                class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
                :class="[
                  state.labelType === 'contact'
                    ? 'bg-n-brand text-white shadow-sm'
                    : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
                ]"
                @click="state.labelType = 'contact'"
              >
                {{ $t('CAMPAIGN.BUHOTEXT.FORM.LABEL_TYPE.CONTACT') }}
              </button>
            </div>
            <p class="text-xs text-n-slate-11">
              {{ $t('CAMPAIGN.BUHOTEXT.FORM.LABEL_TYPE.HELP') }}
            </p>
          </div>

          <!-- Selector de etiquetas -->
          <TagMultiSelectComboBox
            v-model="state.selected_labels"
            :label="$t('CAMPAIGN.BUHOTEXT.FORM.SELECTED_LABELS.LABEL')"
            :placeholder="
              $t('CAMPAIGN.BUHOTEXT.FORM.SELECTED_LABELS.PLACEHOLDER')
            "
            :options="labelsOptions"
          />

          <div
            class="text-xs text-n-slate-11 bg-n-blue-3 border border-n-blue-6 rounded-lg p-3"
          >
            <i class="i-lucide-info mr-1" />
            {{ $t('CAMPAIGN.BUHOTEXT.FORM.SELECTED_LABELS.HELP') }}
          </div>

          <label
            class="flex items-start gap-3 rounded-lg border border-n-weak bg-n-alpha-2 p-3"
          >
            <input
              v-model="state.personalized_send"
              type="checkbox"
              class="mt-0.5 size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
            />
            <span class="text-sm text-n-slate-12">
              <span class="font-medium">{{
                $t('CAMPAIGN.BUHOTEXT.FORM.PERSONALIZED_SEND.LABEL')
              }}</span>
              <span class="block text-xs text-n-slate-11 mt-1">
                {{ $t('CAMPAIGN.BUHOTEXT.FORM.PERSONALIZED_SEND.HELP') }}
              </span>
            </span>
          </label>
        </div>
      </div>

      <!-- Mensaje -->
      <div class="space-y-4">
        <h3
          class="text-sm font-semibold text-n-slate-12 uppercase tracking-wide"
        >
          {{ $t('CAMPAIGN.BUHOTEXT.EDITOR.MESSAGE') }}
        </h3>

        <div>
          <VariableTextArea
            v-if="shouldShowVariableComposer"
            v-model="state.content"
            :label="$t('CAMPAIGN.BUHOTEXT.FORM.MESSAGE.LABEL')"
            :placeholder="$t('CAMPAIGN.BUHOTEXT.FORM.MESSAGE.PLACEHOLDER')"
            :error="formErrors.content"
            :rows="6"
            :variables="variableOptions"
          />
          <TextArea
            v-else
            v-model="state.content"
            :label="$t('CAMPAIGN.BUHOTEXT.FORM.MESSAGE.LABEL')"
            :placeholder="$t('CAMPAIGN.BUHOTEXT.FORM.MESSAGE.PLACEHOLDER')"
            :error="formErrors.content"
            rows="6"
          />
          <div
            class="flex justify-between items-center mt-2 text-xs text-n-slate-11"
          >
            <span>
              {{ $t('CAMPAIGN.BUHOTEXT.FORM.MESSAGE.CHARS_COUNT') }}:
              {{ messageChars.length }}
            </span>
            <span>
              {{ $t('CAMPAIGN.BUHOTEXT.FORM.MESSAGE.SMS_COUNT') }}:
              {{ messageChars.sms }}
            </span>
          </div>
        </div>
      </div>

      <!-- Acciones -->
      <div class="flex gap-3 justify-end pt-4 border-t border-n-weak">
        <Button
          :label="$t('CAMPAIGN.BUHOTEXT.FORM.CANCEL')"
          slate
          @click="emit('cancel')"
        />
        <Button
          :label="submitButtonLabel"
          :disabled="isSubmitDisabled"
          :loading="isSubmitting"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
